import { ConstraintRequiresActivation, OfferFragment } from '@occc/graphql';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import { Operation } from '@occc-app/offerHelpers/types';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { makeCreateRequiresActivationConstraintOperation } from '@occc-app/offerHelpers/operations/createConstraintOperation.utils';
import { makeUpdateRequiresActivationConstraintOperation } from '@occc-app/offerHelpers/operations/updateConstraintOperation.utils';
import { LimitType, hasLimitOfType, transformOfferToLimitFormValues } from '@occc/feat-limits';

import {
  MakeConstraintOperations,
  RequiresActivationConstraint,
  RequiresActivationConstraintFormValues,
  RequiresActivationConstraintWithLimitsFormValues,
} from './types';

export const getRequiresActivationConstraints = (
  offer?: OfferFragment,
): ConstraintRequiresActivation[] =>
  offer?.constraints.filter(constraintHelper.isRequiresActivation) ?? [];

export const hasConfiguredRequiresActivationConstraint = (
  constraint: ConstraintRequiresActivation,
): boolean =>
  !!constraint.mustFulfillWithinDays || constraint.allowMultipleFulfillmentsOnActivation;

const isRequiredActivationSectionEnabled = (
  offer: OfferFragment,
  defaultValues?: Partial<RequiresActivationConstraintFormValues>,
): boolean => {
  const isPreviouslyConfigured = !!offer.earningMechanism;
  const constraintRequiresActivation = getRequiresActivationConstraints(offer)[0];
  const hasLimits = hasLimitOfType(offer.limits, LimitType.Activation);

  if (hasLimits || constraintRequiresActivation) {
    return true;
  }

  if (!isPreviouslyConfigured && defaultValues) {
    return !!defaultValues.isEnabled;
  }

  return false;
};

const getDefaultValues = (
  offer: OfferFragment,
  defaultValues?: Partial<RequiresActivationConstraint>,
) => {
  const isPreviouslyConfigured = !!offer.earningMechanism;
  const includeDefaultValues = !isPreviouslyConfigured && !!defaultValues;

  return includeDefaultValues ? defaultValues : {};
};

export const getInitialValues = (
  offer: OfferFragment,
  defaultValues?: Partial<RequiresActivationConstraint>,
): RequiresActivationConstraint => {
  const constraintRequiresActivation = getRequiresActivationConstraints(offer)[0];

  if (constraintRequiresActivation) {
    return {
      id: constraintRequiresActivation.id,
      allowMultipleFulfillmentsOnActivation:
        constraintRequiresActivation.allowMultipleFulfillmentsOnActivation,
      isMustFulfillWithinEnabled: !!constraintRequiresActivation.mustFulfillWithinDays,
      mustFulfillWithinDays: constraintRequiresActivation.mustFulfillWithinDays ?? undefined,
    };
  }

  return {
    isMustFulfillWithinEnabled: false,
    allowMultipleFulfillmentsOnActivation: false,
    ...getDefaultValues(offer, defaultValues),
  };
};

export const getRequiresActivationInitialValues = (
  offer: OfferFragment,
  defaultValues?: Partial<RequiresActivationConstraintFormValues>,
): RequiresActivationConstraintFormValues => ({
  isEnabled: isRequiredActivationSectionEnabled(offer, defaultValues),
  requiresActivation: getInitialValues(offer, defaultValues?.requiresActivation),
});

export const getRequiresActivationWithLimitsInitialValues = (
  offer: OfferFragment,
  defaultValues?: Partial<RequiresActivationConstraintWithLimitsFormValues>,
): RequiresActivationConstraintWithLimitsFormValues => {
  const isEnabled = isRequiredActivationSectionEnabled(offer, defaultValues);
  const limits = transformOfferToLimitFormValues(offer, LimitType.Activation, {
    global: defaultValues?.global,
    membership: defaultValues?.membership,
  });
  const requiresActivation = getInitialValues(offer, defaultValues?.requiresActivation);

  if (isEnabled) {
    return {
      ...limits,
      isEnabled: true,
      requiresActivation,
    };
  }

  return {
    ...limits,
    isEnabled: false,
    requiresActivation,
  };
};

export const makeRequiresActivationConstraintOperations: MakeConstraintOperations = (
  offerId,
  values,
  _dirtyFields,
  offer,
) => {
  const {
    isEnabled,
    requiresActivation: {
      allowMultipleFulfillmentsOnActivation,
      mustFulfillWithinDays,
      isMustFulfillWithinEnabled,
      id: formConstraintId,
    },
  } = values;

  const operations: Operation[] = [];
  const getMustFulfillWithinDays = isMustFulfillWithinEnabled ? mustFulfillWithinDays : undefined;

  const existingConstraints = getRequiresActivationConstraints(offer);
  const constraintId = formConstraintId ?? existingConstraints[0]?.id;

  existingConstraints
    .filter((constraint) => constraint.id !== constraintId)
    .forEach((constraint) => {
      operations.push(makeDeleteOperation('constraintDelete', constraint.id));
    });

  const createConstraintOperation = makeCreateRequiresActivationConstraintOperation(
    offerId,
    allowMultipleFulfillmentsOnActivation,
    getMustFulfillWithinDays,
  );

  if (constraintId && isEnabled) {
    operations.push(
      makeUpdateRequiresActivationConstraintOperation(
        constraintId,
        allowMultipleFulfillmentsOnActivation,
        getMustFulfillWithinDays,
      ),
    );

    return operations;
  }

  if (constraintId) {
    operations.push(makeDeleteOperation('constraintDelete', constraintId));
  }

  if (isEnabled) {
    operations.push(createConstraintOperation);
  }

  return operations;
};
